/**
 * Type Stage — Stage 1b: Question type validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.2
 *
 * Validates:
 * - questionType is a known type in the registry
 * - Interactive/specialized types have required data.{type} structure
 * - Type-specific data field values (ranges, arrays, consistency)
 * - data.{type} convention compliance (no interactiveConfig)
 *
 * Runs after Schema Stage (1) and before Graph Stage (2).
 * Issues use domain 'type', stage 1 (as "Stage 1b").
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { blocker, coach, info } from '../issueContract'
import {
  QUESTION_TYPE_REGISTRY,
  type QuestionTypeDefinition,
} from '../../questionTypeDefinitions'
import type { Question, QuestionType } from '../../../content'

const STAGE: PipelineStage = 1 // Stage 1b shares stage number with schema

// ─── Data Resolution Helper ───────────────────────────────────────────────────

/**
 * Resolve the effective data object for a question.
 * Supports the nested convention where data is stored under data.{typeName}.
 * E.g., data.money.{mode, targetCents} or data.clock.{mode, target}
 */
function resolveTypeData(question: Question): Record<string, unknown> {
  const data = question.data
  if (!data || typeof data !== 'object') return {}

  const typeName = question.questionType
  if (typeName) {
    const nested = (data as Record<string, unknown>)[typeName]
    if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
      return nested as Record<string, unknown>
    }
  }
  return data as Record<string, unknown>
}

/**
 * Types that have comprehensive type-specific validation and should skip
 * the generic required-field check in validateDataStructure.
 * These validators handle multiple data formats (canonical + legacy).
 */
const TYPES_WITH_COMPREHENSIVE_VALIDATION: Set<string> = new Set([
  'bar_chart',
  'line_chart',
  'matching',
  'pie_chart',
  'text_highlight',
])

// ─── Main Entry Point ─────────────────────────────────────────────────────────

/**
 * Run question type validation on a question.
 * Returns ValidationIssue[] (blocker/coach/info).
 */
export function runTypeStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'
  const qType = question.questionType

  // ── 1. Known type check ──────────────────────────────────────────────────

  if (!qType) {
    // Already caught by schema stage, but guard here too
    return issues
  }

  const typeDef = QUESTION_TYPE_REGISTRY[qType as QuestionType]
  if (!typeDef) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_UNKNOWN',
        `Unknown question type: "${qType}"`,
        {
          field: 'questionType',
          actual: qType,
          questionId: qId,
          familyId: fId,
          suggestion: `Valid types: ${Object.keys(QUESTION_TYPE_REGISTRY).join(', ')}`,
        },
      ),
    )
    return issues // Can't validate further without a type definition
  }

  // ── 2. interactiveConfig deprecation check ───────────────────────────────

  if (question.interactiveConfig !== undefined) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_DEPRECATED_INTERACTIVE_CONFIG',
        'interactiveConfig is deprecated — use data.{type} convention instead',
        {
          field: 'interactiveConfig',
          questionId: qId,
          familyId: fId,
          suggestion: `Move interactive data to question.data object`,
        },
      ),
    )
  }

  // ── 3. Data structure validation for non-standard types ──────────────────

  if (typeDef.category !== 'standard') {
    const dataIssues = validateDataStructure(question, typeDef, qId, fId)
    issues.push(...dataIssues)
  }

  // ── 4. Type-specific deep validation ─────────────────────────────────────

  const deepIssues = validateTypeSpecific(question, typeDef, qId, fId)
  issues.push(...deepIssues)

  return issues
}

// ─── Data Structure Validation ────────────────────────────────────────────────

/**
 * Validate that non-standard types have the required data.{type} structure.
 */
function validateDataStructure(
  question: Question,
  typeDef: QuestionTypeDefinition,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const data = question.data

  // Non-standard types MUST have a data field
  if (!data || typeof data !== 'object') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MISSING_DATA',
        `Type "${question.questionType}" (${typeDef.category}) requires a data object`,
        {
          field: 'data',
          questionId: qId,
          familyId: fId,
          suggestion: `Add a data object with required fields: ${typeDef.dataStructure?.required?.join(', ') ?? 'see type definition'}`,
        },
      ),
    )
    return issues
  }

  // Skip generic required-field check for types with comprehensive validators
  // These validators handle multiple data formats (canonical + legacy + pairs)
  if (TYPES_WITH_COMPREHENSIVE_VALIDATION.has(question.questionType)) {
    return issues
  }

  // Resolve nested data convention (e.g., data.money.{...} or data.clock.{...})
  const resolvedData = resolveTypeData(question)

  // Validate required data subfields against resolved data
  if (typeDef.dataStructure?.required) {
    for (const field of typeDef.dataStructure.required) {
      if (
        !(field in resolvedData) ||
        resolvedData[field] === undefined ||
        resolvedData[field] === null
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MISSING_DATA_FIELD',
            `Required data field "${field}" missing for type "${question.questionType}"`,
            {
              field: `data.${field}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Type-Specific Deep Validation ────────────────────────────────────────────

/**
 * Run type-specific validation rules beyond structural checks.
 */
function validateTypeSpecific(
  question: Question,
  typeDef: QuestionTypeDefinition,
  qId: string,
  fId: string,
): ValidationIssue[] {
  // Resolve nested data convention for type-specific validators
  const resolvedData = resolveTypeData(question)
  const rawData = question.data as Record<string, unknown> | undefined
  if (!resolvedData && typeDef.category === 'standard') return []
  if (!rawData) return [] // Already caught by validateDataStructure

  switch (question.questionType) {
    case 'clock':
      return validateClock(resolvedData, qId, fId)
    case 'money':
      return validateMoney(resolvedData, qId, fId)
    case 'ordering':
      return validateOrdering(resolvedData, qId, fId)
    case 'matching':
      return validateMatching(rawData, qId, fId)
    case 'table_reading':
      return validateTableReading(resolvedData, qId, fId)
    case 'bar_chart':
      return validateBarChart(rawData, qId, fId)
    case 'line_chart':
      return validateLineChart(rawData, qId, fId)
    case 'pie_chart':
      return validatePieChart(rawData, qId, fId)
    case 'schedule_reading':
      return validateScheduleReading(resolvedData, qId, fId)
    case 'ratio_table':
      return validateRatioTable(resolvedData, qId, fId)
    case 'number_sequence':
      return validateNumberSequence(resolvedData, qId, fId)
    case 'text_highlight':
      return validateTextHighlight(resolvedData, qId, fId)
    case 'drag_drop_fill':
      return validateDragDropFill(resolvedData, qId, fId)
    case 'fill_in':
      return validateFillIn(question, qId, fId)
    case 'cijferend_optellen':
    case 'cijferend_aftrekken':
    case 'cijferend_vermenigvuldigen':
    case 'cijferend_delen':
      return validateColumnArithmetic(
        resolvedData,
        question.questionType,
        qId,
        fId,
      )
    default:
      return []
  }
}

// ─── Clock Validation ─────────────────────────────────────────────────────────

function validateClock(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Mode validation
  const validModes = [
    'read_analog_fill',
    'read_digital_fill',
    'analog_to_digital_mc',
    'digital_to_analog_mc',
  ]
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_CLOCK_INVALID_MODE',
        `Invalid clock mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // Target validation
  const target = data.target as Record<string, unknown> | undefined
  if (target && typeof target === 'object') {
    const hours = target.hours as number
    const minutes = target.minutes as number

    if (
      typeof hours !== 'number' ||
      hours < 0 ||
      hours > 23 ||
      !Number.isInteger(hours)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_HOURS',
          `Clock hours must be integer 0-23, got: ${hours}`,
          {
            field: 'data.target.hours',
            expected: '0-23',
            actual: String(hours),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (
      typeof minutes !== 'number' ||
      minutes < 0 ||
      minutes > 59 ||
      !Number.isInteger(minutes)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_INVALID_MINUTES',
          `Clock minutes must be integer 0-59, got: ${minutes}`,
          {
            field: 'data.target.minutes',
            expected: '0-59',
            actual: String(minutes),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  // MC modes require options
  if (data.mode && String(data.mode).endsWith('_mc')) {
    if (
      !data.options ||
      !Array.isArray(data.options) ||
      (data.options as unknown[]).length < 2
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_CLOCK_MC_MISSING_OPTIONS',
          'Clock MC mode requires options array with ≥2 entries',
          {
            field: 'data.options',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Money Validation ─────────────────────────────────────────────────────────

function validateMoney(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Mode validation
  const validModes = ['make_amount', 'give_change']
  if (data.mode && !validModes.includes(data.mode as string)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_INVALID_MODE',
        `Invalid money mode: "${data.mode}"`,
        {
          field: 'data.mode',
          expected: validModes.join(' | '),
          actual: String(data.mode),
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // targetCents validation
  const targetCents = data.targetCents as number
  if (typeof targetCents === 'number') {
    if (targetCents <= 0 || !Number.isInteger(targetCents)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_INVALID_TARGET',
          `targetCents must be positive integer, got: ${targetCents}`,
          {
            field: 'data.targetCents',
            expected: 'positive integer',
            actual: String(targetCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  // allowedDenoms validation
  const denoms = data.allowedDenoms
  if (denoms && Array.isArray(denoms)) {
    for (let i = 0; i < (denoms as unknown[]).length; i++) {
      const d = (denoms as Record<string, unknown>[])[i]
      if (!d || typeof d !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM',
            `allowedDenoms[${i}] must be an object`,
            {
              field: `data.allowedDenoms[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
        continue
      }
      if (typeof d.valueCents !== 'number' || (d.valueCents as number) <= 0) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_VALUE',
            `allowedDenoms[${i}].valueCents must be positive number`,
            {
              field: `data.allowedDenoms[${i}].valueCents`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
      if (d.type !== 'coin' && d.type !== 'bill') {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_MONEY_INVALID_DENOM_TYPE',
            `allowedDenoms[${i}].type should be "coin" or "bill"`,
            {
              field: `data.allowedDenoms[${i}].type`,
              expected: 'coin | bill',
              actual: String(d.type),
              questionId: qId,
              familyId: fId,
              severity: 40,
            },
          ),
        )
      }
    }
  } else if (denoms !== undefined) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MONEY_DENOMS_NOT_ARRAY',
        'allowedDenoms must be an array',
        {
          field: 'data.allowedDenoms',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  // give_change mode requires priceCents and paidCents
  if (data.mode === 'give_change') {
    if (
      typeof data.priceCents !== 'number' ||
      (data.priceCents as number) <= 0
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PRICE',
          'give_change mode requires positive priceCents',
          {
            field: 'data.priceCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    if (typeof data.paidCents !== 'number' || (data.paidCents as number) <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_MISSING_PAID',
          'give_change mode requires positive paidCents',
          {
            field: 'data.paidCents',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    // paidCents should be >= priceCents
    if (
      typeof data.paidCents === 'number' &&
      typeof data.priceCents === 'number' &&
      (data.paidCents as number) < (data.priceCents as number)
    ) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_MONEY_PAID_LESS_THAN_PRICE',
          'paidCents must be ≥ priceCents for give_change',
          {
            field: 'data.paidCents',
            expected: `≥ ${data.priceCents}`,
            actual: String(data.paidCents),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Ordering Validation ──────────────────────────────────────────────────────

function validateOrdering(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const items = data.items
  const correctOrder = data.correctOrder

  if (
    items &&
    Array.isArray(items) &&
    correctOrder &&
    Array.isArray(correctOrder)
  ) {
    const itemsArr = items as unknown[]
    const orderArr = correctOrder as number[]

    // correctOrder must be same length as items
    if (orderArr.length !== itemsArr.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ORDERING_LENGTH_MISMATCH',
          `correctOrder length (${orderArr.length}) must match items length (${itemsArr.length})`,
          {
            field: 'data.correctOrder',
            expected: String(itemsArr.length),
            actual: String(orderArr.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    // correctOrder must be a valid permutation
    const sorted = [...orderArr].sort((a, b) => a - b)
    const expected = Array.from({ length: itemsArr.length }, (_, i) => i)
    if (JSON.stringify(sorted) !== JSON.stringify(expected)) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_ORDERING_INVALID_PERMUTATION',
          'correctOrder must be a permutation of [0, 1, 2, ...]',
          {
            field: 'data.correctOrder',
            expected: `[${expected.join(', ')}]`,
            actual: `[${orderArr.join(', ')}]`,
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    // Items must be non-empty strings
    for (let i = 0; i < itemsArr.length; i++) {
      if (
        typeof itemsArr[i] !== 'string' ||
        (itemsArr[i] as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_ORDERING_EMPTY_ITEM',
            `items[${i}] must be a non-empty string`,
            {
              field: `data.items[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Matching Validation ──────────────────────────────────────────────────────

function validateMatching(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Support multiple matching data formats:
  // 1. Canonical: data.leftItems + data.rightItems + data.correctMatches
  // 2. Pairs format: data.pairs = [{left, right}, ...]
  // 3. Nested pairs: data.matching.pairs = [{left, right}, ...]

  // Try to find pairs in various locations
  const rawPairs =
    (data.pairs as unknown[] | undefined) ??
    ((data.matching as Record<string, unknown> | undefined)?.pairs as
      | unknown[]
      | undefined)

  if (rawPairs && Array.isArray(rawPairs)) {
    // Pairs format validation
    if (rawPairs.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_MATCHING_TOO_FEW',
          'Matching should have at least 2 pairs',
          {
            field: 'data.pairs',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }

    for (let i = 0; i < rawPairs.length; i++) {
      const pair = rawPairs[i] as Record<string, unknown> | undefined
      if (!pair || typeof pair !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_MATCHING_INVALID_PAIR',
            `pairs[${i}] must be an object with left and right`,
            {
              field: `data.pairs[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (
          typeof pair.left !== 'string' ||
          (pair.left as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_EMPTY_LEFT',
              `pairs[${i}].left must be a non-empty string`,
              {
                field: `data.pairs[${i}].left`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (
          typeof pair.right !== 'string' ||
          (pair.right as string).trim() === ''
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_EMPTY_RIGHT',
              `pairs[${i}].right must be a non-empty string`,
              {
                field: `data.pairs[${i}].right`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }

    return issues
  }

  // Canonical format: leftItems + rightItems + correctMatches
  const left = data.leftItems as unknown[] | undefined
  const right = data.rightItems as unknown[] | undefined
  const matches = data.correctMatches as Record<string, number> | undefined

  // If no data format found at all, report missing
  if (!left && !right && !rawPairs) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_MATCHING_NO_DATA',
        'Matching type requires either data.pairs or data.leftItems + data.rightItems + data.correctMatches',
        {
          field: 'data',
          questionId: qId,
          familyId: fId,
          suggestion:
            'Add data.pairs = [{left: "...", right: "..."}, ...] or data.leftItems + data.rightItems + data.correctMatches',
        },
      ),
    )
    return issues
  }

  if (left && Array.isArray(left) && right && Array.isArray(right)) {
    if (left.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_MATCHING_TOO_FEW',
          'Matching should have at least 2 pairs',
          {
            field: 'data.leftItems',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }

    if (matches && typeof matches === 'object') {
      for (const [leftIdx, rightIdx] of Object.entries(matches)) {
        const li = Number(leftIdx)
        const ri = rightIdx
        if (li < 0 || li >= left.length) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_INVALID_LEFT_IDX',
              `correctMatches key ${leftIdx} out of range [0, ${left.length - 1}]`,
              {
                field: `data.correctMatches.${leftIdx}`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
        if (typeof ri !== 'number' || ri < 0 || ri >= right.length) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_MATCHING_INVALID_RIGHT_IDX',
              `correctMatches value for key ${leftIdx} out of range [0, ${right.length - 1}]`,
              {
                field: `data.correctMatches.${leftIdx}`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Table Reading Validation ─────────────────────────────────────────────────

function validateTableReading(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[][] | undefined

  if (headers && Array.isArray(headers) && rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!Array.isArray(row)) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TABLE_ROW_NOT_ARRAY',
            `rows[${i}] must be an array`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (row.length !== headers.length) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_TABLE_ROW_LENGTH_MISMATCH',
            `rows[${i}] has ${row.length} cells but headers has ${headers.length}`,
            {
              field: `data.rows[${i}]`,
              expected: String(headers.length),
              actual: String(row.length),
              questionId: qId,
              familyId: fId,
              severity: 55,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Bar Chart Validation ─────────────────────────────────────────────────────

function validateBarChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Support both formats:
  // 1. Canonical: data.data = [{label, value}, ...]
  // 2. Legacy: data.labels = [...], data.values = [...]
  const dataArray = data.data as unknown[] | undefined
  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined

  if (dataArray && Array.isArray(dataArray)) {
    // Canonical format: array of {label, value}
    for (let i = 0; i < dataArray.length; i++) {
      const entry = dataArray[i] as Record<string, unknown> | undefined
      if (!entry || typeof entry !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_BAR_CHART_INVALID_ENTRY',
            `data.data[${i}] must be an object with label and value`,
            { field: `data.data[${i}]`, questionId: qId, familyId: fId },
          ),
        )
      } else {
        if (
          typeof entry.label !== 'string' ||
          (entry.label as string).trim() === ''
        ) {
          issues.push(
            coach(
              'type',
              STAGE,
              'TYPE_BAR_CHART_EMPTY_LABEL',
              `data.data[${i}].label should be a non-empty string`,
              {
                field: `data.data[${i}].label`,
                questionId: qId,
                familyId: fId,
                severity: 45,
              },
            ),
          )
        }
        if (typeof entry.value !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
              `data.data[${i}].value must be a number`,
              {
                field: `data.data[${i}].value`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
    if (dataArray.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_BAR_CHART_TOO_FEW',
          'Bar chart should have at least 2 data entries',
          { field: 'data.data', questionId: qId, familyId: fId, severity: 40 },
        ),
      )
    }
  } else if (
    labels &&
    Array.isArray(labels) &&
    values &&
    Array.isArray(values)
  ) {
    // Legacy format: separate arrays
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_BAR_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_BAR_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            { field: `data.values[${i}]`, questionId: qId, familyId: fId },
          ),
        )
      }
    }
  } else if (!dataArray && !labels) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_BAR_CHART_NO_DATA',
        'Bar chart requires either data.data array or data.labels + data.values arrays',
        { field: 'data.data', questionId: qId, familyId: fId },
      ),
    )
  }

  return issues
}

// ─── Line Chart Validation ────────────────────────────────────────────────────

function validateLineChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Support both formats (same as bar chart)
  const dataArray = data.data as unknown[] | undefined
  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined

  if (dataArray && Array.isArray(dataArray)) {
    for (let i = 0; i < dataArray.length; i++) {
      const entry = dataArray[i] as Record<string, unknown> | undefined
      if (!entry || typeof entry !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_LINE_CHART_INVALID_ENTRY',
            `data.data[${i}] must be an object with label and value`,
            { field: `data.data[${i}]`, questionId: qId, familyId: fId },
          ),
        )
      } else {
        if (typeof entry.value !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
              `data.data[${i}].value must be a number`,
              {
                field: `data.data[${i}].value`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  } else if (
    labels &&
    Array.isArray(labels) &&
    values &&
    Array.isArray(values)
  ) {
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_LINE_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_LINE_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            { field: `data.values[${i}]`, questionId: qId, familyId: fId },
          ),
        )
      }
    }
  } else if (!dataArray && !labels) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_LINE_CHART_NO_DATA',
        'Line chart requires either data.data array or data.labels + data.values arrays',
        { field: 'data.data', questionId: qId, familyId: fId },
      ),
    )
  }

  return issues
}

// ─── Schedule Reading Validation ──────────────────────────────────────────────

function validateScheduleReading(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[][] | undefined

  if (headers && Array.isArray(headers) && rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!Array.isArray(row)) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_SCHEDULE_ROW_NOT_ARRAY',
            `rows[${i}] must be an array`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (row.length !== headers.length) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_SCHEDULE_ROW_LENGTH_MISMATCH',
            `rows[${i}] has ${row.length} cells but headers has ${headers.length}`,
            {
              field: `data.rows[${i}]`,
              expected: String(headers.length),
              actual: String(row.length),
              questionId: qId,
              familyId: fId,
              severity: 55,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Ratio Table Validation ───────────────────────────────────────────────────

function validateRatioTable(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const headers = data.headers as unknown[] | undefined
  const rows = data.rows as unknown[] | undefined
  const hiddenCells = data.hiddenCells as unknown[] | undefined

  if (headers && Array.isArray(headers)) {
    if (headers.length !== 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_HEADERS',
          'Ratio table should have exactly 2 headers',
          {
            field: 'data.headers',
            expected: '2',
            actual: String(headers.length),
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }
  }

  if (rows && Array.isArray(rows)) {
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i]
      if (!Array.isArray(row) || (row as unknown[]).length !== 2) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_ROW_FORMAT',
            `rows[${i}] should be a [number, number] pair`,
            {
              field: `data.rows[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: 50,
            },
          ),
        )
      }
    }
  }

  // Validate hiddenCells array (matches renderer expectations)
  if (!hiddenCells || !Array.isArray(hiddenCells)) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_RATIO_TABLE_NO_HIDDEN_CELLS',
        'Ratio table requires data.hiddenCells array',
        {
          field: 'data.hiddenCells',
          questionId: qId,
          familyId: fId,
          suggestion: 'Add data.hiddenCells = [{row: 0, col: 1}, ...]',
        },
      ),
    )
  } else {
    if (hiddenCells.length === 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_RATIO_TABLE_EMPTY_HIDDEN_CELLS',
          'Ratio table must have at least one hidden cell',
          {
            field: 'data.hiddenCells',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    // Validate each hidden cell has row and col
    for (let i = 0; i < hiddenCells.length; i++) {
      const cell = hiddenCells[i] as Record<string, unknown> | undefined
      if (!cell || typeof cell !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_RATIO_TABLE_INVALID_HIDDEN_CELL',
            `hiddenCells[${i}] must be an object with row and col`,
            {
              field: `data.hiddenCells[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else {
        if (typeof cell.row !== 'number' || typeof cell.col !== 'number') {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_RATIO_TABLE_INVALID_CELL_COORDS',
              `hiddenCells[${i}] must have numeric row and col`,
              {
                field: `data.hiddenCells[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }

        // Validate row/col are within bounds
        if (rows && Array.isArray(rows)) {
          const rowIdx = cell.row as number
          const colIdx = cell.col as number
          if (rowIdx < 0 || rowIdx >= rows.length) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_ROW_OUT_OF_BOUNDS',
                `hiddenCells[${i}].row (${rowIdx}) out of range [0, ${rows.length - 1}]`,
                {
                  field: `data.hiddenCells[${i}].row`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
          if (colIdx < 0 || colIdx > 1) {
            issues.push(
              blocker(
                'type',
                STAGE,
                'TYPE_RATIO_TABLE_COL_OUT_OF_BOUNDS',
                `hiddenCells[${i}].col (${colIdx}) must be 0 or 1`,
                {
                  field: `data.hiddenCells[${i}].col`,
                  questionId: qId,
                  familyId: fId,
                },
              ),
            )
          }
        }
      }
    }
  }

  return issues
}

// ─── Number Sequence Validation ───────────────────────────────────────────────

function validateNumberSequence(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const sequence = data.sequence as unknown[] | undefined
  const missingIndex = data.missingIndex as number | undefined

  if (sequence && Array.isArray(sequence)) {
    if (sequence.length < 3) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_NUMBER_SEQ_TOO_SHORT',
          'Number sequence should have at least 3 elements',
          {
            field: 'data.sequence',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }

    if (typeof missingIndex === 'number') {
      if (missingIndex < 0 || missingIndex >= sequence.length) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_NUMBER_SEQ_INVALID_INDEX',
            `missingIndex ${missingIndex} out of range [0, ${sequence.length - 1}]`,
            {
              field: 'data.missingIndex',
              expected: `0-${sequence.length - 1}`,
              actual: String(missingIndex),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  if (data.correctValue === undefined || data.correctValue === null) {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_NUMBER_SEQ_NO_ANSWER',
        'Number sequence requires correctValue',
        {
          field: 'data.correctValue',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  }

  return issues
}

// ─── Text Highlight Validation ────────────────────────────────────────────────

function validateTextHighlight(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // Support two formats:
  // 1. Position-based: data.text + data.correctHighlights = [{start, end}, ...]
  // 2. Segments-based: data.segments = [{id, text, isCorrect, isTappable}, ...]

  const segments = data.segments as unknown[] | undefined

  if (segments && Array.isArray(segments)) {
    // Segments format validation
    if (segments.length < 2) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_TOO_FEW_SEGMENTS',
          'text_highlight should have at least 2 segments',
          {
            field: 'data.segments',
            questionId: qId,
            familyId: fId,
            severity: 40,
          },
        ),
      )
    }

    const hasCorrect = segments.some((seg) => {
      const s = seg as Record<string, unknown> | undefined
      return s && s.isCorrect === true
    })
    if (!hasCorrect) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_NO_CORRECT_SEGMENT',
          'At least one segment must have isCorrect: true',
          {
            field: 'data.segments',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i] as Record<string, unknown> | undefined
      if (!seg || typeof seg !== 'object') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_INVALID_SEGMENT',
            `segments[${i}] must be an object`,
            {
              field: `data.segments[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (typeof seg.text !== 'string') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_SEGMENT_NO_TEXT',
            `segments[${i}].text must be a string`,
            {
              field: `data.segments[${i}].text`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }

    return issues
  }

  // Position-based format: data.text + data.correctHighlights
  const text = data.text
  const highlights = data.correctHighlights as unknown[] | undefined

  if (typeof text !== 'string' || (text as string).trim() === '') {
    // No text AND no segments — report missing data
    if (!segments) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_TEXT_HIGHLIGHT_NO_DATA',
          'text_highlight requires either data.text + data.correctHighlights or data.segments',
          {
            field: 'data',
            questionId: qId,
            familyId: fId,
            suggestion:
              'Add data.segments = [{id, text, isCorrect, isTappable}, ...] or data.text + data.correctHighlights',
          },
        ),
      )
    }
    return issues
  }

  if (highlights && Array.isArray(highlights)) {
    for (let i = 0; i < highlights.length; i++) {
      const h = highlights[i] as Record<string, unknown> | undefined
      if (
        !h ||
        typeof h !== 'object' ||
        typeof h.start !== 'number' ||
        typeof h.end !== 'number'
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_TEXT_HIGHLIGHT_INVALID_RANGE',
            `correctHighlights[${i}] must have numeric start and end`,
            {
              field: `data.correctHighlights[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      } else if (typeof text === 'string') {
        if (
          (h.start as number) < 0 ||
          (h.end as number) > text.length ||
          (h.start as number) >= (h.end as number)
        ) {
          issues.push(
            blocker(
              'type',
              STAGE,
              'TYPE_TEXT_HIGHLIGHT_OUT_OF_RANGE',
              `correctHighlights[${i}] range [${h.start}, ${h.end}) invalid for text length ${text.length}`,
              {
                field: `data.correctHighlights[${i}]`,
                questionId: qId,
                familyId: fId,
              },
            ),
          )
        }
      }
    }
  }

  return issues
}

// ─── Drag Drop Fill Validation ────────────────────────────────────────────────

function validateDragDropFill(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const template = data.template
  const draggables = data.draggables as unknown[] | undefined
  const placements = data.correctPlacements as
    | Record<string, number>
    | undefined

  if (typeof template !== 'string' || (template as string).trim() === '') {
    issues.push(
      blocker(
        'type',
        STAGE,
        'TYPE_DRAG_DROP_NO_TEMPLATE',
        'drag_drop_fill requires non-empty data.template',
        {
          field: 'data.template',
          questionId: qId,
          familyId: fId,
        },
      ),
    )
  } else {
    // Check for placeholders {0}, {1}, etc.
    const placeholderMatches = (template as string).match(/\{(\d+)\}/g)
    if (!placeholderMatches || placeholderMatches.length === 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_DRAG_DROP_NO_PLACEHOLDERS',
          'Template must contain {0}, {1}, etc. placeholders',
          {
            field: 'data.template',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (draggables && Array.isArray(draggables)) {
    for (let i = 0; i < draggables.length; i++) {
      if (
        typeof draggables[i] !== 'string' ||
        (draggables[i] as string).trim() === ''
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_DRAG_DROP_EMPTY_DRAGGABLE',
            `draggables[${i}] must be a non-empty string`,
            {
              field: `data.draggables[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  if (
    placements &&
    typeof placements === 'object' &&
    draggables &&
    Array.isArray(draggables)
  ) {
    for (const [placeholderIdx, draggableIdx] of Object.entries(placements)) {
      if (
        typeof draggableIdx !== 'number' ||
        draggableIdx < 0 ||
        draggableIdx >= draggables.length
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_DRAG_DROP_INVALID_PLACEMENT',
            `correctPlacements[${placeholderIdx}] value ${draggableIdx} out of draggables range [0, ${draggables.length - 1}]`,
            {
              field: `data.correctPlacements.${placeholderIdx}`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }
  }

  return issues
}

// ─── Fill In Validation ───────────────────────────────────────────────────────

function validateFillIn(
  question: Question,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  // fill_in uses correctAnswer as string (not index)
  // The schema stage checks correctAnswer for MC, but fill_in is different
  if (
    question.correctAnswer !== undefined &&
    typeof question.correctAnswer === 'number' &&
    (!question.options || question.options.length === 0)
  ) {
    issues.push(
      info(
        'type',
        STAGE,
        'TYPE_FILL_IN_NUMERIC_ANSWER',
        'fill_in type has numeric correctAnswer but no options — ensure answer validation handles this',
        {
          field: 'correctAnswer',
          questionId: qId,
          familyId: fId,
          severity: 15,
        },
      ),
    )
  }

  return issues
}

// ─── Column Arithmetic Validation ─────────────────────────────────────────────

function validateColumnArithmetic(
  data: Record<string, unknown>,
  type: string,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const op1 = data.operand1 as number | undefined
  const op2 = data.operand2 as number | undefined

  if (typeof op1 === 'number' && typeof op2 === 'number') {
    if (op1 <= 0 || op2 <= 0) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_NEGATIVE_OPERAND',
          'Column arithmetic operands must be positive',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (type === 'cijferend_aftrekken' && op1 < op2) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_COLUMN_SUB_NEGATIVE_RESULT',
          'For subtraction, operand1 must be ≥ operand2',
          {
            field: 'data.operand1',
            expected: `≥ ${op2}`,
            actual: String(op1),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    if (!Number.isInteger(op1) || !Number.isInteger(op2)) {
      issues.push(
        coach(
          'type',
          STAGE,
          'TYPE_COLUMN_NON_INTEGER',
          'Column arithmetic typically uses integers',
          {
            field: 'data.operand1',
            questionId: qId,
            familyId: fId,
            severity: 35,
          },
        ),
      )
    }
  }

  return issues
}

// ─── Pie Chart Validation ─────────────────────────────────────────────────────

function validatePieChart(
  data: Record<string, unknown>,
  qId: string,
  fId: string,
): ValidationIssue[] {
  const issues: ValidationIssue[] = []

  const labels = data.labels as unknown[] | undefined
  const values = data.values as unknown[] | undefined
  const correctIndex = data.correctIndex

  if (labels && Array.isArray(labels) && values && Array.isArray(values)) {
    if (labels.length !== values.length) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_PIE_CHART_LENGTH_MISMATCH',
          `labels (${labels.length}) and values (${values.length}) must have same length`,
          {
            field: 'data.values',
            expected: String(labels.length),
            actual: String(values.length),
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }

    for (let i = 0; i < values.length; i++) {
      if (typeof values[i] !== 'number') {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_PIE_CHART_VALUE_NOT_NUMBER',
            `values[${i}] must be a number`,
            {
              field: `data.values[${i}]`,
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    }

    for (let i = 0; i < labels.length; i++) {
      if (
        typeof labels[i] !== 'string' ||
        (labels[i] as string).trim() === ''
      ) {
        issues.push(
          coach(
            'type',
            STAGE,
            'TYPE_PIE_CHART_EMPTY_LABEL',
            `labels[${i}] should be a non-empty string`,
            {
              field: `data.labels[${i}]`,
              questionId: qId,
              familyId: fId,
              severity: 45,
            },
          ),
        )
      }
    }

    // correctIndex validation
    if (typeof correctIndex === 'number') {
      if (
        correctIndex < 0 ||
        correctIndex >= labels.length ||
        !Number.isInteger(correctIndex)
      ) {
        issues.push(
          blocker(
            'type',
            STAGE,
            'TYPE_PIE_CHART_INVALID_CORRECT_INDEX',
            `correctIndex ${correctIndex} out of range [0, ${labels.length - 1}]`,
            {
              field: 'data.correctIndex',
              expected: `0-${labels.length - 1}`,
              actual: String(correctIndex),
              questionId: qId,
              familyId: fId,
            },
          ),
        )
      }
    } else if (correctIndex === undefined || correctIndex === null) {
      issues.push(
        blocker(
          'type',
          STAGE,
          'TYPE_PIE_CHART_MISSING_CORRECT_INDEX',
          'Pie chart requires data.correctIndex',
          {
            field: 'data.correctIndex',
            questionId: qId,
            familyId: fId,
          },
        ),
      )
    }
  }

  if (labels && Array.isArray(labels) && labels.length < 2) {
    issues.push(
      coach(
        'type',
        STAGE,
        'TYPE_PIE_CHART_TOO_FEW_SLICES',
        'Pie chart should have at least 2 slices',
        {
          field: 'data.labels',
          questionId: qId,
          familyId: fId,
          severity: 40,
        },
      ),
    )
  }

  return issues
}
